<div class="agpr-curationAgentActions">
    <div class="agpr-curationAgentActions-header">
        <div class="agpr-curationAgentActions-header-title">
            <h3 class="agpr-curationAgentActions-heading">{{ 'AutomationAgents.panel.actions' | localize }}</h3>
        </div>

        <p class="agpr-curationAgentActions-description">{{ 'AutomationAgents.panel.actions.description' | localize }}</p>
    </div>


    <!--
        One list per outcome: which list an action sits in is what tells the agent when to run it.
        A group only appears once it holds something, so a new agent opens on the add card alone.
    -->
    @for (outcome of outcomeList; track outcome) {
        <section class="agpr-curationAgentActions-group">
            <div class="agpr-curationAgentActions-groupHeader">
                <h4 class="agpr-curationAgentActions-groupHeading">{{ outcomeLabel(outcome) | localize }}</h4>
                <agpr-button
                    [id]="id + '_' + outcome + '_add'"
                    [style]="buttonTypes.icon"
                    icon="fa-regular fa-duotone fa-square-plus"
                    tooltip="AutomationAgents.panel.actions.add"
                    tooltipPosition="left"
                    [ariaLabel]="'AutomationAgents.panel.actions.add' | localize"
                    [disabled]="showAddCard"
                    (click)="openAddCard(outcome)"
                ></agpr-button>
            </div>

            @if (!actionsFor(outcome).length) {
                <div class="agpr-curationAgentActions-empty">
                    <agpr-message
                        [severity]="messageSeverity.noContent"
                    >
                        {{ 'AutomationAgents.panel.actions.empty' | localize }}
                    </agpr-message>
                </div>
            } @else {
                <ul class="agpr-curationAgentActions-list">
                    @for (action of actionsFor(outcome); track $index) {
                        @let editing = isEditing(outcome, $index);
                        <li>
                            <div
                                class="agpr-curationAgentActions-card agpr-contextMenu-parent"
                                [class.agpr-curationAgentActions-card--editing]="editing"
                            >
                                <div class="agpr-curationAgentActions-card-summary">
                                    <span class="agpr-curationAgentActions-card-icon" [ngClass]="action.icon" aria-hidden="true"></span>
                                    <span class="agpr-curationAgentActions-card-name">
                                        {{ 'AutomationAgents.action.card.' + action.type | localize }}{{ action.detailLabel ? ':' : '' }}
                                    </span>
                                    @if (action.detailLabel) {
                                        <span class="agpr-curationAgentActions-card-detail">{{ action.detailLabel | localize }}</span>
                                    }
                                </div>

                                <div class="agpr-contextMenu top right agpr-curationAgentActions-card-controls">
                                    <agpr-button
                                        [id]="id + '.edit.' + outcome + '.' + $index"
                                        [style]="buttonTypes.contextMenu"
                                        icon="fa-regular fa-duotone fa-pen"
                                        tooltip="AutomationAgents.panel.actions.edit"
                                        tooltipPosition="left"
                                        (click)="edit(outcome, $index)"
                                    ></agpr-button>
                                    <agpr-button
                                        [id]="id + '.remove.' + outcome + '.' + $index"
                                        [style]="buttonTypes.contextMenu"
                                        icon="fa-regular fa-duotone fa-trash-can"
                                        [tooltip]="action.autoAdded ? 'AutomationAgents.panel.actions.remove.disabled' : 'AutomationAgents.panel.actions.remove'"
                                        tooltipPosition="left"
                                        [disabled]="action.autoAdded"
                                        (click)="remove(outcome, $index)"
                                    ></agpr-button>
                                </div>

                                @if (editing) {
                                    <div class="agpr-curationAgentActions-card-editor">
                                        <ng-container *ngTemplateOutlet="actionForm"></ng-container>
                                        <div class="agpr-curationAgentActions-controls">
                                            <agpr-button
                                                [id]="id + '.saveDraft'"
                                                [style]="buttonTypes.tertiary"
                                                label="AutomationAgents.panel.actions.saveButton"
                                                [disabled]="!draftIsValid"
                                                (click)="commitDraft()"
                                            ></agpr-button>
                                        </div>
                                    </div>
                                }
                            </div>
                        </li>
                    }
                </ul>
            }

            @if (showAddCard && draftOutcome === outcome) {
                <div class="agpr-curationAgentActions-addCard">
                    <div class="agpr-curationAgentActions-addCard-header">
                        <h4 class="agpr-curationAgentActions-addCard-heading">
                            {{ 'AutomationAgents.panel.actions.addNew' | localize }}
                        </h4>
                        <agpr-button
                            [id]="id + '_cancelDraft'"
                            [style]="buttonTypes.icon"
                            tooltip="AutomationAgents.panel.actions.cancelButton"
                            [icon]="'fa-regular fa-duotone fa-xmark'"
                            (click)="closeEditor()"
                        ></agpr-button>
                    </div>

                    <ng-container *ngTemplateOutlet="actionForm"></ng-container>

                    <div class="agpr-curationAgentActions-controls">
                        <agpr-button
                            [id]="id + '_commitDraft'"
                            [style]="buttonTypes.tertiary"
                            label="AutomationAgents.panel.actions.addButton"
                            [disabled]="!draftIsValid"
                            (click)="commitDraft()"
                        ></agpr-button>
                    </div>
                </div>
            }
        </section>
    }
</div>

<!-- Shared by the Add New Action card and the inline editor an existing card expands into -->
<ng-template #actionForm>
    <div class="agpr-curationAgentActions-form">
        <agpr-dropdown
            [id]="id + '.type'"
            label="AutomationAgents.panel.actions.type"
            placeholder="AutomationAgents.panel.actions.type.placeholder"
            [options]="actionTypeOptions"
            [bindToValue]="true"
            [ngModel]="draftType"
            (ngModelChange)="typeChanged($event)"
        ></agpr-dropdown>

        @if (needsFolders && editingIndex != null) {
            <!-- Editing holds one target, so it is a single pick rather than the chip list -->
            <agpr-autocomplete
                [id]="id + '.folder'"
                labelField="name"
                placeholder="AutomationAgents.panel.actions.folder.placeholder"
                [dropdown]="true"
                [clearOnBlur]="false"
                [suggestions]="folderSuggestions$"
                [customOptions]="folderCustomOptions"
                [showHiddenOption]="true"
                [(ngModel)]="folderPick"
                (itemSelected)="folderPicked()"
                (clear)="folderPicked()"
            ></agpr-autocomplete>
        } @else if (needsFolders) {
            <!-- Picker and chips are one control, as the folder and tag pickers are elsewhere -->
            <div class="agpr-curationAgentActions-picker">
                <agpr-autocomplete
                    class="agpr-curationAgentActions-pickerInput"
                    [id]="id + '.folders'"
                    labelField="name"
                    placeholder="AutomationAgents.panel.actions.folder.placeholder"
                    [dropdown]="true"
                    [multiSelection]="true"
                    [clearOnBlur]="true"
                    [suggestions]="folderSuggestions$"
                    [customOptions]="folderCustomOptions"
                    [showHiddenOption]="true"
                    [(ngModel)]="folderSelection"
                    (itemSelected)="folderSelected()"
                ></agpr-autocomplete>
                <agpr-chip-container
                    class="agpr-curationAgentActions-pickerChips"
                    [id]="id + '.folderChips'"
                    [chips]="folderChips"
                    emptyMessage="AutomationAgents.panel.actions.folder.empty"
                    (removeChip)="removeFolderChip($event)"
                ></agpr-chip-container>
            </div>
        }

        @if (needsTags && editingIndex != null) {
            <agpr-autocomplete
                [id]="id + '.tag'"
                labelField="name"
                placeholder="AutomationAgents.panel.actions.tag.placeholder"
                [dropdown]="true"
                [clearOnBlur]="false"
                [suggestions]="tagSuggestions$"
                [customOptions]="tagCustomOptions"
                [showHiddenOption]="true"
                [(ngModel)]="tagPick"
                (itemSelected)="tagPicked()"
                (clear)="tagPicked()"
            ></agpr-autocomplete>
        } @else if (needsTags) {
            <div class="agpr-curationAgentActions-picker">
                <agpr-autocomplete
                    class="agpr-curationAgentActions-pickerInput"
                    [id]="id + '.tags'"
                    labelField="name"
                    placeholder="AutomationAgents.panel.actions.tag.placeholder"
                    [dropdown]="true"
                    [multiSelection]="true"
                    [clearOnBlur]="true"
                    [suggestions]="tagSuggestions$"
                    [customOptions]="tagCustomOptions"
                    [showHiddenOption]="true"
                    [(ngModel)]="tagSelection"
                    (itemSelected)="tagSelected()"
                ></agpr-autocomplete>
                <agpr-chip-container
                    class="agpr-curationAgentActions-pickerChips"
                    [id]="id + '.tagChips'"
                    [chips]="tagChips"
                    emptyMessage="AutomationAgents.panel.actions.tag.empty"
                    (removeChip)="removeTagChip($event)"
                ></agpr-chip-container>
            </div>
        }

        @if (draftType === actionTypes.generateInsight) {
            <!-- The same insight list the mention insight flow offers, so an agent generates the
                 same things by hand or on a schedule -->
            <agpr-dropdown
                [id]="id + '.insightType'"
                label="AutomationAgents.panel.actions.insightType"
                placeholder="AutomationAgents.panel.actions.insightType.placeholder"
                [options]="insightTypeOptions"
                [bindToValue]="true"
                [ngModel]="draftInsightType"
                (ngModelChange)="insightTypeChanged($event)"
            ></agpr-dropdown>

            @if (hasInsightSettings) {
                <div class="agpr-curationAgentActions-settings">
                    <div class="agpr-curationAgentActions-settingsHeader">
                        <h5 class="agpr-curationAgentActions-settingsHeading">
                            {{ 'AutomationAgents.panel.actions.insightSettings' | localize }}
                        </h5>
                        @if (isCustomInsight) {
                            <agpr-button
                                [id]="id + '.manageInsights'"
                                [style]="buttonTypes.icon"
                                icon="fa-regular fa-duotone fa-gear"
                                tooltip="AutomationAgents.panel.actions.insight.manage"
                                tooltipPosition="left"
                                [ariaLabel]="'AutomationAgents.panel.actions.insight.manage' | localize"
                                (click)="openInsightManager()"
                            ></agpr-button>
                        }
                    </div>

                    <div class="agpr-curationAgentActions-settingsBody">
                        @if (isCustomInsight) {
                            <agpr-dropdown
                                [id]="id + '.customAnalysis'"
                                label="AutomationAgents.panel.actions.customAnalysis"
                                placeholder="AutomationAgents.panel.actions.customAnalysis.placeholder"
                                [options]="customAnalysisOptions"
                                [bindToValue]="true"
                                [(ngModel)]="selectedCustomAnalysis"
                            ></agpr-dropdown>

                            <!-- Writing a new one in place; it is saved as a prompt when Add is pressed -->
                            @if (isNewCustomAnalysis) {
                                <agpr-text-input
                                    [id]="id + '.customAnalysisName'"
                                    label="Mention.workbench.insights.prompt.custom.name"
                                    placeholder="Mention.workbench.insights.prompt.custom.name.placeholder"
                                    [maxLength]="64"
                                    [required]="true"
                                    [(ngModel)]="selectedCustomAnalysis.name"
                                ></agpr-text-input>
                                <agpr-textarea-input
                                    [id]="id + '.customAnalysisPrompt'"
                                    label="Mention.workbench.insights.prompt.custom.prompt"
                                    placeholder="Mention.workbench.insights.prompt.custom.prompt.tooltip"
                                    ariaLabel="Mention.workbench.insights.prompt.custom.prompt.tooltip"
                                    icon="fa-kit-duotone fa-agpr-ai-generic"
                                    [rows]="7"
                                    [required]="true"
                                    [(ngModel)]="selectedCustomAnalysis.prompt"
                                ></agpr-textarea-input>
                            }
                        } @else {
                            <agpr-dropdown
                                [id]="id + '.insightLanguage'"
                                label="Common.dialog.aiHelper.language"
                                [options]="languageOptions"
                                [bindToValue]="true"
                                infoCircleTooltip="Common.dialog.aiHelper.languageTooltip"
                                [(ngModel)]="draftLanguage"
                            ></agpr-dropdown>

                            <!-- Only Contextual Analysis and Sentiment & Opinion are written about
                                 something; the rest work from the mention alone -->
                            @if (draftReadsContext) {
                                <agpr-text-input-chips
                                    [id]="id + '.insightKeywords'"
                                    header="Mention.insightsExplorerTab.quickInsights.analysisContext.label"
                                    placeholder="Mention.insightsExplorerTab.quickInsights.analysisContext.placeholder"
                                    [(ngModel)]="draftKeywords"
                                ></agpr-text-input-chips>
                            }
                        }
                    </div>
                </div>
            }
        }

        @if (draftType === actionTypes.sendToRss) {
            <div class="agpr-inputGroupRow agpr-curationAgentActions-inputRow">
                <div class="agpr-inputGroupRow-dropdown">
                    <agpr-autocomplete
                        [id]="id + '.rssFeed'"
                        labelField="name"
                        placeholder="AutomationAgents.panel.actions.rssFeed.placeholder"
                        [dropdown]="true"
                        [clearOnBlur]="false"
                        [suggestions]="rssFeedList$"
                        [(ngModel)]="selectedRssFeed"
                    ></agpr-autocomplete>
                </div>
                <div class="agpr-inputGroupRow-buttons">
                    <agpr-button
                        [id]="id + '.manageRssFeeds'"
                        [style]="buttonTypes.block"
                        icon="fa-regular fa-duotone fa-gear"
                        tooltip="RSSFeeds.manage"
                        tooltipPosition="top"
                        [ariaLabel]="'RSSFeeds.manage' | localize"
                        (click)="openRssFeedManager()"
                    ></agpr-button>
                </div>
            </div>
        }
    </div>
</ng-template>
